// src/components/pages/Usuario/PaginaUsuario.js
//
// Página do usuário — só para ADM (rota /perfil, protegida em App.js com
// <RotaPrivada perfisPermitidos={['adm']}/>). Mostra o plano de
// assinatura da escola e as ações possíveis:
//
//   - "Gerenciar no Stripe": abre o Customer Portal do Stripe (só aparece
//     se a escola já tem um stripeCustomerId — ou seja, "se houver a
//     opção", como pedido).
//   - "Trocar de plano": troca o price da MESMA subscription (upgrade/
//     downgrade), via Cloud Function trocarAssinatura.
//   - "Cancelar assinatura" / "Reativar assinatura": alterna
//     cancel_at_period_end via cancelarAssinatura.
//   - Se a escola nunca assinou (sem stripeSubscriptionId), mostra os
//     planos pra ASSINAR pela primeira vez (mesmo fluxo do cadastro,
//     createCheckoutSession).
//
// Todo o estado de assinatura vem em tempo real de escolas/{idEscola}
// (onSnapshot) — depois de voltar do Stripe (portal ou checkout), a tela
// se atualiza sozinha assim que o webhook grava a mudança, sem precisar
// de F5.

import { useEffect, useState } from 'react'
import { doc, onSnapshot } from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { getAuth, signOut } from 'firebase/auth'
import { useNavigate } from 'react-router-dom'
import { BiUserCircle } from 'react-icons/bi'
import { CreditCard, ExternalLink, RefreshCcw, XCircle, KeyRound } from 'lucide-react'

import { db } from '../../../firebase/firebase'
import { useAuth } from '../../../hooks/AuthContext'
import { useToast } from '../../../hooks/Toast'
import { planos } from '../../../stripePlanos'
import BackButton from '../../layout/BackButton'
import Button from '../../layout/Button/Button'
import AlterarSenhaModal from '../../layout/AlterarSenha/AlterarSenhaModal'
import geralStyle from '../Geral/geral.module.css'
import style from './usuario.module.css'

function formatarData(timestamp) {
  if (!timestamp) return null
  try {
    return timestamp.toDate().toLocaleDateString('pt-BR')
  } catch {
    return null
  }
}

function nomeDoPlano(planoId) {
  return planos.find((p) => p.id === planoId)?.nome || planoId || '—'
}

export default function PaginaUsuario() {
  const { usuario } = useAuth()
  const { mostrarAviso } = useToast()
  const navigate = useNavigate()

  const [escola, setEscola] = useState(null)
  const [carregando, setCarregando] = useState(true)
  const [processando, setProcessando] = useState(null) // 'portal' | 'cancelar' | 'reativar' | 'trocar:<id>' | 'assinar:<id>' | null
  const [trocandoPlano, setTrocandoPlano] = useState(false)
  const [modalSenhaAberto, setModalSenhaAberto] = useState(false)

  useEffect(() => {
    if (!usuario?.idEscola) return
    const escolaRef = doc(db, 'escolas', usuario.idEscola)
    const cancelarInscricao = onSnapshot(
      escolaRef,
      (snap) => {
        setEscola(snap.exists() ? snap.data() : null)
        setCarregando(false)
      },
      (error) => {
        console.error('Erro ao ler dados da escola:', error)
        mostrarAviso('Erro ao carregar dados da assinatura', 'erro')
        setCarregando(false)
      }
    )
    return () => cancelarInscricao()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuario?.idEscola])

  const assinatura = escola?.assinatura || {}
  const temAssinatura = !!assinatura.stripeSubscriptionId
  const temCustomerId = !!assinatura.stripeCustomerId

  const abrirPortal = async () => {
    setProcessando('portal')
    try {
      const chamar = httpsCallable(getFunctions(), 'createBillingPortalSession')
      const resultado = await chamar({ idEscola: usuario.idEscola })
      window.location.href = resultado.data.url
    } catch (error) {
      mostrarAviso('Erro ao abrir o painel do Stripe: ' + error.message, 'erro')
      setProcessando(null)
    }
  }

  const assinar = async (plano) => {
    setProcessando('assinar:' + plano.id)
    try {
      const chamar = httpsCallable(getFunctions(), 'createCheckoutSession')
      const resultado = await chamar({
        priceId: plano.precoId,
        idEscola: usuario.idEscola,
        planoId: plano.id,
      })
      window.location.href = resultado.data.url
    } catch (error) {
      mostrarAviso('Erro ao iniciar pagamento: ' + error.message, 'erro')
      setProcessando(null)
    }
  }

  const trocarDePlano = async (plano) => {
    if (plano.id === assinatura.plano) return
    if (!window.confirm(`Trocar para o plano ${plano.nome}? A cobrança será ajustada proporcionalmente (proration) já na próxima fatura.`)) return

    setProcessando('trocar:' + plano.id)
    try {
      const chamar = httpsCallable(getFunctions(), 'trocarAssinatura')
      await chamar({ idEscola: usuario.idEscola, novoPriceId: plano.precoId, novoPlanoId: plano.id })
      mostrarAviso('Plano atualizado com sucesso', 'sucesso')
      setTrocandoPlano(false)
    } catch (error) {
      mostrarAviso('Erro ao trocar de plano: ' + error.message, 'erro')
    } finally {
      setProcessando(null)
    }
  }

  const cancelarOuReativar = async (reativar) => {
    const confirmacao = reativar
      ? 'Reativar a renovação automática desta assinatura?'
      : 'Cancelar a assinatura? A escola mantém acesso até o fim do período já pago, mas não vai renovar depois disso.'
    if (!window.confirm(confirmacao)) return

    setProcessando(reativar ? 'reativar' : 'cancelar')
    try {
      const chamar = httpsCallable(getFunctions(), 'cancelarAssinatura')
      await chamar({ idEscola: usuario.idEscola, reativar })
      mostrarAviso(reativar ? 'Renovação reativada' : 'Cancelamento agendado para o fim do período', 'sucesso')
    } catch (error) {
      mostrarAviso('Erro ao atualizar assinatura: ' + error.message, 'erro')
    } finally {
      setProcessando(null)
    }
  }

  const sair = async () => {
    await signOut(getAuth())
    navigate('/login', { replace: true })
  }

  if (carregando) return <p>Carregando...</p>

  return (
    <section className={`${geralStyle.container}`}>
      <BackButton />
      <h3>Minha conta</h3>

      <div className={style.cardConta}>
        <BiUserCircle size={48} />
        <div>
          <p className={style.nome}>{usuario?.nome}</p>
          <p className={style.detalhe}>{usuario?.email} · Diretor/Secretário</p>
        </div>
        <Button variant="outline" icon={KeyRound} onClick={() => setModalSenhaAberto(true)} style={{ marginLeft: 'auto' }}>
          Alterar senha
        </Button>
      </div>

      <AlterarSenhaModal aberto={modalSenhaAberto} onFechar={() => setModalSenhaAberto(false)} />

      <div className={style.card}>
        <h2>Assinatura</h2>

        {!temAssinatura ? (
          <>
            <p className={style.detalhe}>Sua escola ainda não tem uma assinatura ativa. Escolha um plano para liberar o acesso completo:</p>
            <div className={style.gradePlanos}>
              {planos.map((plano) => (
                <div key={plano.id} className={style.cardPlano}>
                  <h4>{plano.nome}</h4>
                  <p className={style.preco}>R$ {plano.preco}<span>{plano.limite}</span></p>
                  <Button
                    onClick={() => assinar(plano)}
                    disabled={processando === 'assinar:' + plano.id}
                  >
                    {processando === 'assinar:' + plano.id ? 'Abrindo...' : 'Assinar'}
                  </Button>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className={style.statusLinha}>
              <span className={`${style.badge} ${assinatura.status === 'ativo' ? style.badgeAtivo : style.badgeInativo}`}>
                {assinatura.status === 'ativo' ? 'Ativa' : 'Inativa'}
              </span>
              <p><strong>Plano atual:</strong> {nomeDoPlano(assinatura.plano)}</p>
            </div>

            {assinatura.cancelamentoAgendado ? (
              <p className={style.avisoCancelamento}>
                Cancelamento agendado — acesso liberado até {formatarData(assinatura.proximaCobranca) || 'o fim do período atual'}.
              </p>
            ) : (
              assinatura.proximaCobranca && (
                <p className={style.detalhe}>Próxima cobrança: {formatarData(assinatura.proximaCobranca)}</p>
              )
            )}

            <div className={style.botoes}>
              {temCustomerId && (
                <Button onClick={abrirPortal} icon={ExternalLink} disabled={processando === 'portal'}>
                  {processando === 'portal' ? 'Abrindo...' : 'Gerenciar no Stripe'}
                </Button>
              )}

              <Button variant="outline" icon={CreditCard} onClick={() => setTrocandoPlano(!trocandoPlano)}>
                Trocar de plano
              </Button>

              {assinatura.cancelamentoAgendado ? (
                <Button variant="outline" icon={RefreshCcw} onClick={() => cancelarOuReativar(true)} disabled={processando === 'reativar'}>
                  {processando === 'reativar' ? 'Reativando...' : 'Reativar assinatura'}
                </Button>
              ) : (
                <Button variant="danger" icon={XCircle} onClick={() => cancelarOuReativar(false)} disabled={processando === 'cancelar'}>
                  {processando === 'cancelar' ? 'Cancelando...' : 'Cancelar assinatura'}
                </Button>
              )}
            </div>

            {trocandoPlano && (
              <div className={style.gradePlanos}>
                {planos.map((plano) => (
                  <div key={plano.id} className={`${style.cardPlano} ${plano.id === assinatura.plano ? style.cardPlanoAtual : ''}`}>
                    <h4>{plano.nome}</h4>
                    <p className={style.preco}>R$ {plano.preco}<span>{plano.limite}</span></p>
                    {plano.id === assinatura.plano ? (
                      <span className={style.detalhe}>Plano atual</span>
                    ) : (
                      <Button
                        variant="outline"
                        onClick={() => trocarDePlano(plano)}
                        disabled={processando === 'trocar:' + plano.id}
                      >
                        {processando === 'trocar:' + plano.id ? 'Trocando...' : 'Escolher'}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <Button variant="outline" onClick={sair} style={{ marginTop: 24 }}>Sair</Button>
    </section>
  )
}